import styled from 'styled-components';
import React, {FC} from 'react';
import {Link} from 'gatsby';

import {
  transition,
  mediaBreakpointDown,
  mediaBreakpointUp,
  fontWeights
} from '../../../../lib/styleVariables';
import {
  fontSizesMixinsR4,
  paletteR4,
  fontFamiliesR4,
  fontSizesMixinsMobileR4
} from '../../../../lib/styleVariablesR4';
import {SocialMediaLinks, SocialMediaLink} from '../../socialMedia/widget';
import {Icon} from '../../../molecules/icon';

interface FooterLink {
  title: string;
  href: string;
  isBold?: boolean;
}

interface FooterColumnProps {
  title: string;
  links: Array<FooterLink>;
}

export interface FooterProps {
  content: FooterColumnProps[];
  socialMediaLinks: SocialMediaLink[];
  bottomLinks: FooterLink[];
  copyrightText?: string;
  isDark?: boolean;
}

const StyledFooterDiv = styled.div`
  font-family: ${fontFamiliesR4.body};
  align-self: center;
  width: 100%;
  max-width: 1320px;
  margin: 0 auto;
  padding: 80px 64px 40px;
  ${mediaBreakpointDown('tablet')} {
    padding: 40px 32px;
  }
`;

const StyledListWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  ${mediaBreakpointDown('desktop')} {
    flex-direction: column;
  }
`;

const StyledLogoWrapper = styled.div`
  margin-right: 92px;

  ${mediaBreakpointDown('desktop')} {
    margin-right: 0;
    margin-bottom: 40px;
  }
`;

const StyledLogo = styled(Icon)`
  width: 68px;
  height: 100px;
`;

const StyledColumns = styled.div`
  display: flex;
  flex: 1;
`;

const StyledColumnsInner = styled.div`
  display: flex;

  ${mediaBreakpointUp('desktop')} {
    &:not(:last-child) {
      margin-right: 60px;
    }

    &:first-child {
      flex: 2;
    }

    &:last-child {
      flex: 3;
    }
  }

  ${mediaBreakpointDown('tablet')} {
    flex-direction: column;
    flex: 1 0 0;

    &:not(:last-child) {
      margin-right: 32px;
    }
  }
`;

const StyledListColumn = styled.div`
  ${mediaBreakpointUp('desktop')} {
    flex: 1;
    & + & {
      margin-left: 60px;
    }
  }

  ${mediaBreakpointDown('tablet')} {
    &:not(:first-child) {
      margin-top: 48px;
    }
  }
`;

const StyledListColumnTitle = styled.h5`
  ${fontSizesMixinsR4.h7()};
  text-transform: uppercase;
  color: ${paletteR4.red.lowlight};
  margin-top: 0;
  margin-bottom: 48px;

  ${mediaBreakpointDown('tablet')} {
    ${fontSizesMixinsMobileR4.h6()};
    margin-bottom: 30px;
  }
`;

const StyledListColumnLinksList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

const StyledListColumnLinksListItem = styled.li`
  ${fontSizesMixinsR4.p2()};

  &:not(:last-child) {
    margin-bottom: 22px;
  }

  ${mediaBreakpointDown('tablet')} {
    ${fontSizesMixinsMobileR4.p4()};

    &:not(:last-child) {
      margin-bottom: 30px;
    }
  }

  [data-theme='dark'] & {
    color: ${paletteR4.white.base};
  }
`;

const commonFooterLinkStyle = `
  color: currentColor;
  text-decoration: none;
  transition: color ${transition.fast};
`;

const StyledLink = styled.a<{isBold: boolean}>`
  ${commonFooterLinkStyle};
  font-weight: ${props => (props.isBold ? fontWeights.bold : fontWeights.normal)};
`;

const StyledBottomDiv = styled.div`
  ${fontSizesMixinsR4.p2()};
  color: ${paletteR4.black.base};

  ${mediaBreakpointUp('desktop')} {
    margin-top: 130px;
  }

  ${mediaBreakpointDown('tablet')} {
    flex-direction: column;
    align-items: flex-start;
    ${fontSizesMixinsMobileR4.p4()};
  }

  [data-theme='dark'] & {
    color: ${paletteR4.white.base};
  }
`;

const StyledBottomLinksWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${mediaBreakpointDown('tablet')} {
    flex-direction: column-reverse;
    align-items: flex-start;
  }
`;

const StyledCopyright = styled.div`
  color: ${paletteR4.black.base};
  ${mediaBreakpointDown('tablet')} {
    margin-top: 40px;
  }

  [data-theme='dark'] & {
    color: ${paletteR4.white.base};
  }
`;

const StyledSocialMediaLinksWrapper = styled.div`
  display: flex;
  color: ${paletteR4.black.base};
`;

const StyledLineBreak = styled.div`
  width: 100%;
  height: 1px;
  margin-top: 80px;
  margin-bottom: 40px;
  background: ${paletteR4.gray.base};
  border-radius: 10px;
  [data-theme='dark'] & {
    background: ${paletteR4.white.base};
  }

  ${mediaBreakpointDown('tablet')} {
    margin-top: 56px;
    margin-bottom: 48px;
  }
`;

const StyledFooterNav = styled.nav`
  ${mediaBreakpointDown('tablet')} {
    margin-top: 48px;
    display: flex;
    flex-direction: column;
  }
`;

const StyledNavLink = styled.a`
  ${commonFooterLinkStyle}

  &:not(:last-child) {
    margin-right: 50px;
  }

  ${mediaBreakpointDown('tablet')} {
    &:not(:last-child) {
      margin-bottom: 30px;
    }
  }
`;

const socialMediaLinkStyles = `
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  [data-theme="dark"] & {
    color: ${paletteR4.white.base};
  }
`;

export const Footer: FC<FooterProps> = props => {
  const splitFooterMenu = (arr: any) => {
    const indexToSplit = Math.floor(arr.length / 2);
    const first = arr.slice(0, indexToSplit);
    const second = arr.slice(indexToSplit);

    return [first, second];
  };

  const [footerMenuFirstBatch, footerMenuSecondBatch] = splitFooterMenu(props.content);

  return (
    <StyledFooterDiv>
      <StyledListWrapper>
        <StyledLogoWrapper>
          <Link to="/">
            <StyledLogo name={props.isDark ? 'r4HomeLogoWhiteBig' : 'r4HomeLogoBig'} useR4 />
          </Link>
        </StyledLogoWrapper>
        <StyledColumns>
          <StyledColumnsInner>
            {footerMenuFirstBatch.map((column: any) => (
              <StyledListColumn key={column.title}>
                <StyledListColumnTitle>{column.title}</StyledListColumnTitle>
                <StyledListColumnLinksList>
                  {column.links.map((link: any) => (
                    <StyledListColumnLinksListItem key={link.title}>
                      <StyledLink href={link.href} isBold={link.isBold || false}>
                        {link.title}
                      </StyledLink>
                    </StyledListColumnLinksListItem>
                  ))}
                </StyledListColumnLinksList>
              </StyledListColumn>
            ))}
          </StyledColumnsInner>
          <StyledColumnsInner>
            {footerMenuSecondBatch.map((column: any) => (
              <StyledListColumn key={column.title}>
                <StyledListColumnTitle>{column.title}</StyledListColumnTitle>
                <StyledListColumnLinksList>
                  {column.links.map((link: any) => (
                    <StyledListColumnLinksListItem key={link.title}>
                      <StyledLink href={link.href} isBold={link.isBold || false}>
                        {link.title}
                      </StyledLink>
                    </StyledListColumnLinksListItem>
                  ))}
                </StyledListColumnLinksList>
              </StyledListColumn>
            ))}
          </StyledColumnsInner>
        </StyledColumns>
      </StyledListWrapper>
      <StyledBottomDiv>
        <StyledLineBreak />
        <StyledBottomLinksWrapper>
          <StyledCopyright>{props.copyrightText}</StyledCopyright>
          <StyledFooterNav>
            {props.bottomLinks.map(link => (
              <StyledNavLink key={link.title} href={link.href}>
                {link.title}
              </StyledNavLink>
            ))}
          </StyledFooterNav>
          <StyledSocialMediaLinksWrapper>
            <SocialMediaLinks
              links={props.socialMediaLinks}
              size={25}
              $css={socialMediaLinkStyles}
              designVersion="r4"
            />
          </StyledSocialMediaLinksWrapper>
        </StyledBottomLinksWrapper>
      </StyledBottomDiv>
    </StyledFooterDiv>
  );
};
